import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Info,
  Clapperboard,
  Star,
  Link2,
  CircleDot,
  Image as ImageIcon,
  ImagePlus,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateMovie } from "@/hooks/useMovies";
import { useUploadImage } from "@/hooks/useImages";
import { fromLocalDatetimeInput } from "@/lib/utils";
import { toast } from "sonner";
import type { EventStatus } from "@/types";

const STATUSES: { value: EventStatus; label: string }[] = [
  { value: "approved", label: "Zatwierdzone" },
  { value: "pending", label: "Oczekujące" },
  { value: "rejected", label: "Odrzucone" },
];

const EMPTY = {
  title: "",
  originalTitle: "",
  description: "",
  date: "",
  year: "",
  runtime: "",
  room: "",
  countryCode: "",
  language: "",
  filmwebRating: "",
  imdbRating: "",
  rtScore: "",
  filmwebUrl: "",
  ticketUrl: "",
};

interface CreateMovieDialogProps {
  /** Controlled open state. When provided, the default trigger button is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Deferred poster picker — holds the File locally until the movie exists. */
function PosterPicker({
  file,
  onSelect,
  onClear,
}: {
  file: File | null;
  onSelect: (file: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Derive the object URL from the file; revoke the previous one on change/unmount.
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onSelect(f);
        }}
      />
      {file && previewUrl ? (
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={previewUrl}
              alt="Podgląd plakatu"
              className="h-[88px] w-[62px] rounded-md object-cover"
            />
            <button
              type="button"
              onClick={onClear}
              aria-label="Usuń plakat"
              className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-destructive-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <div className="min-w-0 text-sm">
            <p className="truncate font-medium">{file.name}</p>
            <button
              type="button"
              className="text-pulso-primary hover:underline"
              onClick={() => inputRef.current?.click()}
            >
              Zmień
            </button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
          <ImagePlus className="mr-2 h-4 w-4" />
          Wybierz plakat
        </Button>
      )}
    </div>
  );
}

export function CreateMovieDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateMovieDialogProps = {}) {
  const createMovie = useCreateMovie();
  const uploadImage = useUploadImage();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState<EventStatus>("approved");
  const [posterFile, setPosterFile] = useState<File | null>(null);

  const setOpen = (next: boolean) => {
    if (isControlled) controlledOnOpenChange?.(next);
    else setUncontrolledOpen(next);
    if (!next) {
      setForm(EMPTY);
      setStatus("approved");
      setPosterFile(null);
    }
  };

  const f =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submitting = createMovie.isPending || uploadImage.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const movie = await createMovie.mutateAsync({
        title: form.title,
        date: fromLocalDatetimeInput(form.date) ?? new Date().toISOString(),
        originalTitle: form.originalTitle || undefined,
        description: form.description || undefined,
        status,
        year: form.year ? parseInt(form.year) : null,
        runtime: form.runtime ? parseInt(form.runtime) : null,
        room: form.room || null,
        countryCode: form.countryCode || null,
        language: form.language || null,
        filmwebRating: form.filmwebRating || null,
        imdbRating: form.imdbRating || null,
        rtScore: form.rtScore || null,
        filmwebUrl: form.filmwebUrl || null,
        ticketUrl: form.ticketUrl || null,
      });

      if (posterFile) {
        try {
          await uploadImage.mutateAsync({
            file: posterFile,
            entityType: "movie",
            entityId: movie.id,
            isCover: true,
          });
        } catch (err) {
          // Movie was created; only the poster failed — let the user retry via edit.
          toast.warning("Film dodany, ale nie udało się przesłać plakatu", {
            description: err instanceof Error ? err.message : undefined,
          });
          setOpen(false);
          return;
        }
      }

      toast.success("Film dodany");
      setOpen(false);
    } catch (err) {
      toast.error("Nie udało się dodać filmu", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      {!isControlled && (
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Dodaj film
        </Button>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Nowy film"
        size="lg"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button
              type="submit"
              form="movie-create-form"
              disabled={submitting || !form.title || !form.date}
            >
              {submitting ? "Dodawanie..." : "Utwórz film"}
            </Button>
          </>
        }
      >
        <form id="movie-create-form" onSubmit={handleSubmit} className="space-y-4">
          <FormSection icon={Info} title="Podstawowe informacje">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tytuł (PL)</Label>
                <Input value={form.title} onChange={f("title")} required placeholder="np. Diuna" />
              </div>
              <div className="space-y-2">
                <Label>Tytuł oryginalny</Label>
                <Input
                  value={form.originalTitle}
                  onChange={f("originalTitle")}
                  placeholder="Opcjonalnie"
                />
              </div>
              <div className="space-y-2">
                <Label>Data seansu</Label>
                <Input type="datetime-local" value={form.date} onChange={f("date")} required />
              </div>
              <div className="space-y-2">
                <Label>Opis</Label>
                <Textarea value={form.description} onChange={f("description")} rows={3} />
              </div>
            </div>
          </FormSection>

          <FormSection icon={ImageIcon} title="Plakat">
            <PosterPicker
              file={posterFile}
              onSelect={setPosterFile}
              onClear={() => setPosterFile(null)}
            />
          </FormSection>

          <FormSection icon={Clapperboard} title="Szczegóły seansu">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rok</Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={f("year")}
                  placeholder="np. 2024"
                />
              </div>
              <div className="space-y-2">
                <Label>Czas trwania (min)</Label>
                <Input
                  type="number"
                  value={form.runtime}
                  onChange={f("runtime")}
                  placeholder="np. 120"
                />
              </div>
              <div className="space-y-2">
                <Label>Kraj</Label>
                <Input
                  value={form.countryCode}
                  onChange={f("countryCode")}
                  placeholder="np. PL, US"
                />
              </div>
              <div className="space-y-2">
                <Label>Sala</Label>
                <Input value={form.room} onChange={f("room")} placeholder="np. Sala 1" />
              </div>
              <div className="space-y-2">
                <Label>Język</Label>
                <Input value={form.language} onChange={f("language")} placeholder="np. DUB, NAP" />
              </div>
            </div>
          </FormSection>

          <FormSection icon={Star} title="Oceny">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Filmweb</Label>
                <Input
                  value={form.filmwebRating}
                  onChange={f("filmwebRating")}
                  placeholder="np. 7.5"
                />
              </div>
              <div className="space-y-2">
                <Label>IMDb</Label>
                <Input value={form.imdbRating} onChange={f("imdbRating")} placeholder="np. 7.2" />
              </div>
              <div className="space-y-2">
                <Label>RT</Label>
                <Input value={form.rtScore} onChange={f("rtScore")} placeholder="np. 85%" />
              </div>
            </div>
          </FormSection>

          <FormSection icon={Link2} title="Linki">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>URL Filmweb</Label>
                <Input
                  value={form.filmwebUrl}
                  onChange={f("filmwebUrl")}
                  placeholder="https://filmweb.pl/..."
                />
              </div>
              <div className="space-y-2">
                <Label>Link do zakupu biletów</Label>
                <Input value={form.ticketUrl} onChange={f("ticketUrl")} placeholder="https://..." />
              </div>
            </div>
          </FormSection>

          <FormSection icon={CircleDot} title="Status">
            <Select value={status} onValueChange={(v) => setStatus(v as EventStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormSection>
        </form>
      </FormDialog>
    </>
  );
}
